import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import Modal from '../shared/Modal';

const STATUS_COLORS = {
  in_stock:   'bg-emerald-100 text-emerald-700',
  incoming:   'bg-purple-100 text-purple-700',
  allocated:  'bg-amber-100 text-amber-700',
  deployed:   'bg-blue-100 text-blue-700',
  in_transit: 'bg-sky-100 text-sky-700',
};

function FillBar({ pct }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  const color = p >= 90 ? 'bg-red-500' : p >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${p}%` }} />
      </div>
      <span className={`text-[8px] font-bold ${p >= 90 ? 'text-red-600' : p >= 70 ? 'text-amber-600' : 'text-gray-500'}`}>
        {p}%
      </span>
    </div>
  );
}

function EditFillModal({ location, onClose, onSave }) {
  const [fillPct, setFillPct] = useState(String(location.fill_pct ?? 0));
  const [notes, setNotes]     = useState(location.capacity_notes ?? '');
  const [saving, setSaving]   = useState(false);

  async function save() {
    setSaving(true);
    try {
      await onSave(location.id, {
        name: location.name,
        type: location.type || 'storage',
        fill_pct: parseFloat(fillPct) || 0,
        capacity_notes: notes || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Update Capacity" onClose={onClose}>
        <p className="text-[9px] text-gray-400 mb-4">{location.name}</p>
        <div className="space-y-3">
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Fill % (0–100)</label>
            <input
              type="number" min="0" max="100" step="1"
              className="form-input"
              value={fillPct}
              onChange={e => setFillPct(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Notes (optional)</label>
            <textarea
              className="form-input resize-none"
              rows={2}
              placeholder="e.g. Top shelf full, floor space available"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose}
            className="flex-1 px-3 py-1.5 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="flex-1 px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
    </Modal>
  );
}

export default function InventoryManagerDashboard() {
  const { currentUser, navigate } = useApp();
  const { toast } = useToast();

  const [data, setData]           = useState({ locations: [], incoming: [] });
  const [loading, setLoading]     = useState(true);
  const [editLoc, setEditLoc]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('inventory/location-dashboard')
      .then(r => { setData(r || { locations: [], incoming: [] }); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshOnFocus(load);

  async function saveFill(locId, patch) {
    await api.put(`inventory/locations/${locId}`, patch);
    toast('Capacity updated', 'success');
    load();
  }

  const { locations, incoming } = data;

  // Totals across all locations
  const totals = locations.reduce(
    (acc, l) => ({
      in_stock:   acc.in_stock   + (l.in_stock   || 0),
      incoming:   acc.incoming   + (l.incoming   || 0),
      allocated:  acc.allocated  + (l.allocated  || 0),
      deployed:   acc.deployed   + (l.deployed   || 0),
    }),
    { in_stock: 0, incoming: 0, allocated: 0, deployed: 0 }
  );

  const totalIncomingQty  = incoming.reduce((s, r) => s + (r.quantity || 0), 0);
  const totalIncomingCost = incoming.reduce((s, r) => s + (r.po_line_cost || 0), 0);

  return (
    <div className="fade-in flex flex-col gap-4">

      {/* Header banner */}
      <div className="bg-gradient-to-br from-navy to-[#1a3a5c] rounded-lg px-5 py-3 text-white relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gold/10 pointer-events-none" />
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xs font-bold text-gold-light">
              Welcome back, {currentUser?.first_name || 'Manager'} — Stock Operations
            </h3>
            <p className="text-[9px] text-white/50 mt-0.5">
              {locations.length} active location{locations.length !== 1 ? 's' : ''} ·{' '}
              {totals.in_stock} in stock · {totals.incoming} incoming
            </p>
          </div>
          <button onClick={() => navigate('inventory')}
            className="flex-shrink-0 btn btn-primary text-[10px] px-3 py-1">
            Open Inventory →
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: 'In Stock',  val: totals.in_stock,  color: 'text-emerald-600' },
          { label: 'Incoming',  val: totals.incoming,  color: 'text-purple-600' },
          { label: 'Allocated', val: totals.allocated, color: 'text-amber-600' },
          { label: 'Deployed',  val: totals.deployed,  color: 'text-blue-600' },
        ].map(s => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[8px] text-gray-500 uppercase font-bold">{s.label}</div>
            <div className={`text-2xl font-extrabold ${s.color}`}>{s.val}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Locations */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-[11px] font-bold text-gray-800">Stock by Location</h3>
            <span className="text-[8px] text-gray-400">{locations.length} locations</span>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">{[...Array(4)].map((_,i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : locations.length === 0 ? (
            <div className="p-6 text-center text-[10px] text-gray-400">No active locations</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {locations.map(loc => (
                <div key={loc.id} className="px-3 py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <span className="text-[10px] font-semibold text-gray-800">{loc.name}</span>
                      {loc.type && (
                        <span className="ml-1.5 text-[8px] text-gray-400 italic capitalize">{loc.type}</span>
                      )}
                    </div>
                    <button
                      onClick={() => setEditLoc(loc)}
                      className="text-[8px] text-navy hover:underline font-semibold"
                      title="Update capacity fill %"
                    >
                      ✏ Capacity
                    </button>
                  </div>

                  <FillBar pct={loc.fill_pct} />
                  {loc.capacity_notes && (
                    <div className="text-[8px] text-gray-400 mt-0.5 italic">{loc.capacity_notes}</div>
                  )}

                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {[
                      { key: 'in_stock',   label: 'In Stock',   val: loc.in_stock },
                      { key: 'incoming',   label: 'Incoming',   val: loc.incoming },
                      { key: 'allocated',  label: 'Allocated',  val: loc.allocated },
                      { key: 'deployed',   label: 'Deployed',   val: loc.deployed },
                      { key: 'in_transit', label: 'In Transit', val: loc.in_transit },
                    ].filter(s => s.val > 0).map(s => (
                      <span key={s.key} className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${STATUS_COLORS[s.key]}`}>
                        {s.val} {s.label}
                      </span>
                    ))}
                    {!loc.in_stock && !loc.incoming && !loc.allocated && !loc.deployed && !loc.in_transit && (
                      <span className="text-[8px] text-gray-300 italic">Empty</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Incoming stock summary */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-[11px] font-bold text-gray-800">Incoming Stock</h3>
            <span className="text-[8px] text-gray-400">
              {totalIncomingQty} unit{totalIncomingQty !== 1 ? 's' : ''} · {formatMoney(totalIncomingCost)} est. value
            </span>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">{[...Array(3)].map((_,i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : incoming.length === 0 ? (
            <div className="p-6 text-center text-[10px] text-gray-400">No incoming stock at the moment</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">PO</th>
                    <th className="text-left px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">Supplier</th>
                    <th className="text-left px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">Location</th>
                    <th className="text-right px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">Qty</th>
                    <th className="text-right px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">Est. Cost</th>
                    <th className="text-left px-3 py-1.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50">ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {incoming.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/60">
                      <td className="px-3 py-1.5">
                        {row.source_po_id ? (
                          <button
                            onClick={() => navigate('pos', row.source_po_id)}
                            className="text-[9px] font-mono font-bold text-navy hover:underline"
                          >
                            {row.source_po_number || '—'}
                          </button>
                        ) : (
                          <span className="text-[9px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-[9px] text-gray-600">{row.supplier_name || '—'}</td>
                      <td className="px-3 py-1.5 text-[9px] text-gray-600">{row.destination_location || <span className="text-gray-300 italic">TBC</span>}</td>
                      <td className="px-3 py-1.5 text-right">
                        <span className="text-[10px] font-bold text-purple-700">{row.quantity}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-[9px] text-gray-600">
                        {row.po_line_cost > 0 ? formatMoney(row.po_line_cost) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-[9px] text-gray-600">
                        {row.expected_delivery ? (
                          <span className={row.expected_delivery < new Date().toISOString().split('T')[0] ? 'text-red-600 font-semibold' : ''}>
                            {formatDate(row.expected_delivery)}
                          </span>
                        ) : (
                          <span className="text-gray-300 italic">No ETA</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {incoming.length > 1 && (
                  <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-50">
                      <td colSpan={3} className="px-3 py-1.5 text-[8px] font-bold text-gray-500 uppercase">Total</td>
                      <td className="px-3 py-1.5 text-right text-[10px] font-bold text-purple-700">{totalIncomingQty}</td>
                      <td className="px-3 py-1.5 text-right text-[9px] font-bold text-gray-700">{formatMoney(totalIncomingCost)}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>

      </div>

      {editLoc && (
        <EditFillModal
          location={editLoc}
          onClose={() => setEditLoc(null)}
          onSave={saveFill}
        />
      )}
    </div>
  );
}
